import { getDefaultStaffImage } from "@/utils/avatar.utils";
import { RoleEnum } from "@/types/user.definition";
import { ChevronLeft, ChevronRight, User } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Doctor } from "../../../types/doctor.definition";

interface DoctorListProps {
  doctors: Doctor[];
  className?: string;
}

export const DoctorList: React.FC<DoctorListProps> = ({
  doctors,
  className = "",
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % Math.max(1, doctors.length - 2));
    setTimeout(() => setIsAnimating(false), 300);
  }, [isAnimating, doctors.length]);

  // Auto-scroll every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating && doctors.length > 3) {
        nextSlide();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, isAnimating, doctors.length, nextSlide]);

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) =>
      prev === 0 ? Math.max(0, doctors.length - 3) : prev - 1
    );
    setTimeout(() => setIsAnimating(false), 300);
  };

  const goToSlide = (index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const totalSlides = Math.max(1, doctors.length - 2);

  return (
    <div
      className={`p-6 bg-ews-primary rounded-xl border shadow-soft border-neutral-200 ${className}`}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-6 text-xs sm:text-sm lg:text-base xl:text-lg">
        <h3 className="font-bold text-white">
          {t("dashboard.workingDoctors.title")}
        </h3>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-white rounded-full opacity-80"></div>
          <span className="text-2xl text-white opacity-90">
            {doctors.length}
          </span>
        </div>
      </div>

      {/* Carousel Container */}
      <div className="overflow-hidden relative mb-6">
        {/* Navigation Buttons */}
        {doctors.length > 3 && (
          <>
            <button
              onClick={prevSlide}
              disabled={isAnimating}
              className="flex absolute left-2 top-1/2 z-10 justify-center items-center w-8 h-8 rounded-full backdrop-blur-sm transition-all duration-200 transform -translate-y-1/2 bg-white/20 hover:bg-white/30"
            >
              <ChevronLeft className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={nextSlide}
              disabled={isAnimating}
              className="flex absolute right-2 top-1/2 z-10 justify-center items-center w-8 h-8 rounded-full backdrop-blur-sm transition-all duration-200 transform -translate-y-1/2 bg-white/20 hover:bg-white/30"
            >
              <ChevronRight className="w-4 h-4 text-white" />
            </button>
          </>
        )}

        {/* Doctors Carousel */}
        <div
          className="flex space-x-4 transition-transform duration-300 ease-out"
          style={{
            transform: `translateX(-${currentIndex * (100 / 3)}%)`,
          }}
        >
          {doctors.map((doctor) => (
            <div
              key={doctor.id}
              className={`transition-all duration-300 ${isAnimating ? "scale-95" : "scale-100"
                }`}
            >
              <div className="p-4 rounded-2xl border backdrop-blur-sm transition-all duration-300 cursor-pointer bg-white/10 border-white/20 hover:bg-white/20 hover:scale-105 group">
                {/* Doctor Avatar */}
                <div className="relative">
                  <img
                    src={
                      doctor.photoUrl ||
                      getDefaultStaffImage(RoleEnum.DOCTOR, doctor.civility)
                    }
                    alt={doctor.firstName}
                    className="object-cover mx-auto w-20 h-20 rounded-2xl border-2 transition-all duration-300 border-white/30 group-hover:border-white/50"
                  />
                  <div className="absolute -right-1 -bottom-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination Dots */}
      {doctors.length > 3 && (
        <div className="flex justify-center mb-6 space-x-2">
          {Array.from({ length: totalSlides }, (_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${index === currentIndex
                  ? "bg-white w-4"
                  : "bg-white/40 hover:bg-white/60"
                }`}
            />
          ))}
        </div>
      )}

      {doctors.length === 0 && (
        <div className="py-8 text-center">
          <User className="mx-auto mb-3 w-12 h-12 text-white/60" />
          <p className="text-sm text-white/80">
            {t("dashboard.workingDoctors.none")}
          </p>
        </div>
      )}
    </div>
  );
};
